import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { WorkflowStepper } from '../../components/WorkflowStepper';

const JOURNAL_ENTRY_STEPS = [
  { key: 'draft', label: 'Draft' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'posted', label: 'Posted' },
];

export function JournalEntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const journalId = Number(id);
  const detailQuery = useQuery({ queryKey: ['finance-journal-detail', journalId], queryFn: () => financeApi.getJournalEntry(journalId) });

  if (!detailQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const { data: entry, lines } = detailQuery.data;

  const totalDebit = lines.reduce((sum, l) => sum + Number(l.debit_amount), 0);
  const totalCredit = lines.reduce((sum, l) => sum + Number(l.credit_amount), 0);

  return (
    <div className="page">
      <Link to="/finance/journal-entries">&larr; Back to Journal Entries</Link>
      <div className="page-header">
        <h1>{entry.journal_reference}</h1>
        <div className="icon-action-group">
          <IconActionButton icon={Printer} label="Print" href={financeApi.journalEntryPdfUrl(journalId)} />
        </div>
      </div>
      <p className="muted">{entry.entry_date} · {entry.description} · <StatusBadge status={entry.status} /></p>

      <section className="card">
        <WorkflowStepper
          steps={JOURNAL_ENTRY_STEPS}
          currentStatus={entry.status}
          detourLabel={entry.status === 'returned' ? 'Returned for Redraft' : entry.status === 'rejected' ? 'Rejected' : entry.status === 'reversed' ? 'Reversed' : undefined}
          reachedIndex={entry.status === 'returned' ? (entry.reviewed_at ? 2 : 1) : entry.status === 'rejected' ? 2 : entry.status === 'reversed' ? 4 : undefined}
        />
      </section>

      {entry.reversal_of_journal_id && (
        <p className="muted">This is a reversal of <Link to={`/finance/journal-entries/${entry.reversal_of_journal_id}`}>journal #{entry.reversal_of_journal_id}</Link>.</p>
      )}
      {entry.reversed_by_journal_id && (
        <p className="muted">This journal was reversed by <Link to={`/finance/journal-entries/${entry.reversed_by_journal_id}`}>journal #{entry.reversed_by_journal_id}</Link>. Reason: {entry.reversal_reason}</p>
      )}

      <section className="card">
        <h2>Lines</h2>
        <DataTable
          rows={lines}
          rowKey={(l) => l.id}
          emptyMessage="No lines."
          columns={[
            { header: 'Account', render: (l) => `${l.account_code} - ${l.account_name}` },
            { header: 'Description', render: (l) => l.description ?? '-' },
            { header: 'Debit', render: (l) => Number(l.debit_amount) > 0 ? Number(l.debit_amount).toLocaleString() : '-' },
            { header: 'Credit', render: (l) => Number(l.credit_amount) > 0 ? Number(l.credit_amount).toLocaleString() : '-' },
          ]}
        />
        <p><strong>Total: Debit {totalDebit.toLocaleString()} · Credit {totalCredit.toLocaleString()}</strong> {totalDebit === totalCredit ? '(Balanced)' : '(Not Balanced)'}</p>
      </section>

      {entry.return_comment && <p className="muted">Return comment: {entry.return_comment}</p>}
      {entry.decision_comment && <p className="muted">Decision comment: {entry.decision_comment}</p>}
    </div>
  );
}
